import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Button, {
  ButtonType,
} from '../../../../../../../../../components/common/button';
import Select from '../../../../../../../../../components/common/select';
import { useDidMount } from '../../../../../../../../../components/common/effects';
import { Visibility } from '../../../../../../../../../types/common-types';
import './style.scss';

enum CalculatorType {
  DEFAULT = 'default',
  WITH_SERVIICE = 'withService',
}
enum ProductType {
  ARKILAINA = 'arkilaina',
  SATASENLAINA = 'satasenlaina',
}
interface CalculatorValue {
  amount: string;
  voluntaryFeeValue: string;
  product: ProductType;
}
interface Labels {
  arkilaina: string;
  satasenlaina: string;
  loanAmount: string;
  loanInterest: string;
  loanInterestDescr: string;
  maturityText: string;
  maturityMos: string;
  interestRate: string;
  APR: string;
  editButton: string;
  saveButton: string;
  selectLoanAmount: string;
  calculatorCreditDetails: string;
  principalText: string;
  interestText: string;
  mgmtText: string;
  totalText: string;
  voluntaryFeeText: string;
  calculatorFooterText: string;
  calculatorBodyText: string;
}
interface Settings {
  amount: { min: number; max: number; step: number };
  amountOptions: {
    [key: string]: {
      [key: string]: {
        interestAmount: number;
        wdFeeAmount: number;
        arpValue: number;
      };
    };
  };
  satasenlaina: {
    amount: string;
    duration: number;
    wdFeeAmount: number;
    interestAmount: number;
    arpValue: string;
    maturityMos: string;
    interestRate: string;
  };
}
interface Props {
  id: string;
  visibility?: Visibility;
  value: CalculatorValue;
  type?: CalculatorType;
  extra: {
    labels: Labels;
    settings: Settings;
    productSelect?: boolean;
    defaultEditMode: boolean;
  };
  onSubmit: (id: string) => void;
  onChange: (
    value: {
      id: string;
      value: object;
      error: boolean;
    },
    callback?: () => void
  ) => void;
}

const Calculator: React.FC<Props> = ({
  id,
  visibility,
  type = CalculatorType.DEFAULT,
  extra,
  value,
  onChange,
  onSubmit,
}) => {
  const {
    labels,
    settings,
    defaultEditMode = false,
    productSelect = true,
  } = extra;
  const {
    arkilaina,
    satasenlaina,
    editButton,
    saveButton,
    loanAmount,
    loanInterest,
    selectLoanAmount,
    maturityText,
    maturityMos: maturityMosArkilaina,
    interestRate: interestRateArkilaina,
    calculatorCreditDetails,
    principalText,
    interestText,
    mgmtText,
    totalText,
    APR,
    voluntaryFeeText,
    calculatorFooterText,
    calculatorBodyText,
  } = labels;
  const {
    amount: settingsAmount,
    amountOptions,
    satasenlaina: sataselainaOptions,
  } = settings;
  const [editMode, setEditMode] = useState(defaultEditMode);
  const { voluntaryFeeValue, product = ProductType.ARKILAINA } = value;
  const didMount = useDidMount();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (didMount) onSubmit(id);
    }, 500);
    return () => clearTimeout(timeout);
  }, [value]);

  let opts;
  let arpValue;
  let interestAmount = 0;
  let mgmtValue = 0;
  let totalValue;
  let maturityMos;
  let interestRate;
  let amount = '';
  if (product === ProductType.ARKILAINA) {
    opts = amountOptions[type];
    amount = value.amount;
    maturityMos = maturityMosArkilaina;
    interestRate = interestRateArkilaina;
    arpValue = opts[`${amount}`].arpValue;
    interestAmount = opts[`${amount}`].interestAmount;
    mgmtValue = opts[`${amount}`].wdFeeAmount;
  } else if (product === ProductType.SATASENLAINA) {
    opts = sataselainaOptions;
    amount = opts.amount;
    maturityMos = opts.maturityMos;
    interestRate = opts.interestRate;
    arpValue = opts.arpValue;
    interestAmount = opts.interestAmount;
    mgmtValue = opts.wdFeeAmount;
  }
  totalValue = +amount + +interestAmount + +mgmtValue;
  if (voluntaryFeeValue && voluntaryFeeValue !== '0')
    totalValue += +voluntaryFeeValue;

  const handleChange = (key: string, val: string) => {
    const newData = { ...value, [key]: val };
    onChange({ id, value: newData, error: false });
  };
  const options = [
    { value: 'arkilaina', title: arkilaina }
  ];
  const productTitle = options.find(i => i.value === product)?.title;
  return (
    <div
      id={id}
      data-cy="calculator"
      styleName={cn('calculator', {
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calculator__header">
        {productSelect ? (
          <Select
            styleName="calculator__header__select"
            value={product}
            options={options}
            onChange={({ value: productValue }) => {
              setEditMode(false);
              handleChange('product', productValue);
            }}
          />
        ) : (
          <span>{productTitle}</span>
        )}
        <Button
          id="calculatorButton"
          styleName={cn('calculator__button', {
            edit: editMode,
            hidden:
              visibility === Visibility.Disabled ||
              product === ProductType.SATASENLAINA,
          })}
          type={ButtonType.Normal}
          label={editMode ? saveButton : editButton}
          onClick={() => {
            setEditMode(!editMode);
          }}
        />
      </div>
      <div styleName="calculator__divider" />
      {editMode ? (
        <div styleName="calculator__editor">
          <div styleName="calculator__editor__item">
            <div styleName="calculator__editor__header">
              <span styleName="calculator__editor__title">
                {selectLoanAmount}
              </span>
              <span styleName="calculator__editor__value">
                {`${amount} `}
                &euro;
              </span>
            </div>
            <div styleName="calculator__editor__slider">
              <input
                type="range"
                data-cy-prop="value.amount"
                min={settingsAmount.min}
                max={settingsAmount.max}
                step={settingsAmount.step}
                value={amount}
                onChange={e => handleChange('amount', e.target.value)}
              />
            </div>
            <div styleName="calculator__editor__footer">
              <span>
                {`${settingsAmount.min} `}
                &euro;
              </span>
              <span>
                {`${settingsAmount.max} `}
                &euro;
              </span>
            </div>
          </div>
        </div>
      ) : null}
      <div styleName="calculator__summary">
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{loanAmount}</span>
          <span styleName="calculator__row__val">
            {`${amount} `}
            &euro;
          </span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{loanInterest}</span>
          <span styleName="calculator__row__val">{interestRate}</span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{APR}</span>
          <span styleName="calculator__row__val">{arpValue}</span>
        </div>
        <div styleName="calculator__row">
          <span styleName="calculator__row__lbl">{maturityText}</span>
          <span styleName="calculator__row__val bold">{maturityMos}</span>
        </div>
      </div>
      {calculatorBodyText ? (
        <div styleName="calculator__body">
          <div>{calculatorBodyText}</div>
        </div>
      ) : null}
      <div styleName="calculator__example">
        <div styleName="calculator__example__row header">
          <b>{calculatorCreditDetails}</b>
        </div>
        <div styleName="calculator__example__row">
          <div styleName="calculator__example__tbl">
            <span>{principalText}</span>
            <span styleName="calculator__example__tbl__val">
              <b>
                {`${amount} `}
                &euro;
              </b>
            </span>
          </div>
          <div styleName="calculator__example__tbl">
            <span>{interestText}</span>
            <span styleName="calculator__example__tbl__val">
              <b>
                {`${String(interestAmount.toFixed(2)).replace('.', ',')} `}
                &euro;
              </b>
            </span>
          </div>
          <div styleName="calculator__example__tbl">
            <span>{mgmtText}</span>
            <span styleName="calculator__example__tbl__val">
              <b>
                {`${String(mgmtValue.toFixed(2)).replace('.', ',')} `}
                &euro;
              </b>
            </span>
          </div>
          {voluntaryFeeValue && voluntaryFeeValue !== '0' ? (
            <div styleName="calculator__example__tbl">
              <span>{voluntaryFeeText}</span>
              <span styleName="calculator__example__tbl__val">
                <b>
                  {`${voluntaryFeeValue} `}
                  &euro;
                </b>
              </span>
            </div>
          ) : null}
          <div styleName="calculator__example__tbl bold">
            <span>{totalText}</span>
            <span styleName="calculator__example__tbl__val bold">
              <b>
                {`${String(totalValue.toFixed(2)).replace('.', ',')} `}
                &euro;
              </b>
            </span>
          </div>
        </div>
        <div styleName="calculator__info">
          <div styleName="calculator__example__row">{calculatorFooterText}</div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
